import { useMemo, useState } from 'react';
import type { Attachment, CommunityEvent } from '@shared/types';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useAsync } from '../lib/useAsync';
import { useToast } from '../lib/toast';
import { dateTimeLabel, fromLocalInput, toLocalInput } from '../lib/format';
import { AttachmentGrid, AttachmentUploader } from '../components/Attachments';
import { NoteThread } from '../components/NoteThread';
import { EmptyState, Field, LoadingPage, Tag } from '../components/ui';
import { PageHeader } from '../components/PageHeader';

type Scope = 'upcoming' | 'past' | 'all';

export function EventsPage() {
  const { canOrganise } = useAuth();
  const { notify, fail } = useToast();
  const [scope, setScope] = useState<Scope>('upcoming');
  const [editing, setEditing] = useState<CommunityEvent | 'new' | null>(null);

  const state = useAsync(async () => (await api.events(scope)).events, [scope]);
  const events = state.data ?? [];

  const months = useMemo(() => groupByMonth(events), [events]);

  function replace(updated: CommunityEvent) {
    state.setData(events.map((event) => (event.id === updated.id ? updated : event)));
  }

  if (state.loading && !state.data) return <LoadingPage label="Loading the calendar…" />;

  return (
    <div>
      <PageHeader
        title="Events"
        lede={
          canOrganise
            ? 'Publish neighborhood events, collect RSVPs, and answer questions in the comments.'
            : 'What is coming up around the neighborhood. RSVP so the organizers know how much to plan for.'
        }
      />

      <div className="row" style={{ marginBottom: 14 }}>
        {canOrganise && (
          <button
            type="button"
            className="btn"
            onClick={() => setEditing((current) => (current === 'new' ? null : 'new'))}
          >
            {editing === 'new' ? 'Close form' : 'Create event'}
          </button>
        )}
      </div>

      <div className="filters" role="tablist" aria-label="Filter events">
        {(['upcoming', 'all', 'past'] as Scope[]).map((option) => (
          <button
            key={option}
            type="button"
            role="tab"
            aria-selected={scope === option}
            className={scope === option ? 'on' : ''}
            onClick={() => setScope(option)}
          >
            {option === 'upcoming' ? 'Upcoming' : option === 'past' ? 'Past' : 'All'}
          </button>
        ))}
      </div>

      {editing && (
        <EventForm
          event={editing === 'new' ? null : editing}
          onSaved={(event) => {
            if (editing === 'new') state.setData([event, ...events]);
            else replace(event);
            setEditing(null);
            notify('Event saved.', 'success');
          }}
          onCancel={() => setEditing(null)}
          onError={fail}
        />
      )}

      {events.length === 0 ? (
        <EmptyState
          title={scope === 'past' ? 'No past events yet.' : 'Nothing on the calendar.'}
          hint={canOrganise ? 'Create the first one above.' : 'Check back soon.'}
        />
      ) : (
        months.map(([label, list]) => (
          <div key={label}>
            <h2>{label}</h2>
            {list.map((event) => (
              <EventCard
                key={event.id}
                event={event}
                onChange={replace}
                onEdit={() => setEditing(event)}
                onDeleted={() => state.setData(events.filter((item) => item.id !== event.id))}
              />
            ))}
          </div>
        ))
      )}
    </div>
  );
}

function groupByMonth(events: CommunityEvent[]): [string, CommunityEvent[]][] {
  const map = new Map<string, CommunityEvent[]>();
  for (const event of events) {
    const label = new Date(event.startsAt).toLocaleDateString('en-US', {
      month: 'long',
      year: 'numeric',
    });
    const list = map.get(label) ?? [];
    list.push(event);
    map.set(label, list);
  }
  return [...map.entries()];
}

/* --------------------------------------------------------------------- form */

function EventForm({
  event,
  onSaved,
  onCancel,
  onError,
}: {
  event: CommunityEvent | null;
  onSaved: (event: CommunityEvent) => void;
  onCancel: () => void;
  onError: (error: unknown, fallback?: string) => void;
}) {
  const [form, setForm] = useState({
    title: event?.title ?? '',
    description: event?.description ?? '',
    location: event?.location ?? '',
    startsAt: toLocalInput(event?.startsAt ?? null),
    endsAt: toLocalInput(event?.endsAt ?? null),
    allDay: event?.allDay ?? false,
    rsvpEnabled: event?.rsvpEnabled ?? true,
    rsvpLimit: event?.rsvpLimit?.toString() ?? '',
    commentsEnabled: event?.commentsEnabled ?? true,
    published: event?.published ?? true,
  });
  const [files, setFiles] = useState<Attachment[]>([]);
  const [busy, setBusy] = useState(false);

  async function submit(submitEvent: React.FormEvent) {
    submitEvent.preventDefault();
    const startsAt = fromLocalInput(form.startsAt);
    if (!startsAt) {
      onError(null, 'Pick a start date and time.');
      return;
    }

    setBusy(true);
    const payload = {
      title: form.title,
      description: form.description,
      location: form.location || undefined,
      startsAt,
      endsAt: fromLocalInput(form.endsAt),
      allDay: form.allDay,
      rsvpEnabled: form.rsvpEnabled,
      rsvpLimit: form.rsvpLimit ? Number(form.rsvpLimit) : null,
      commentsEnabled: form.commentsEnabled,
      published: form.published,
      attachmentIds: files.map((f) => f.id),
    };

    try {
      const result = event
        ? await api.eventUpdate(event.id, payload)
        : await api.eventCreate(payload);
      onSaved(result.event);
    } catch (error) {
      onError(error, 'The event was not saved.');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="card">
      <h3>{event ? 'Edit event' : 'Create event'}</h3>
      <form onSubmit={submit}>
        <Field label="Title">
          <input
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder="Fall Block Party"
            required
          />
        </Field>

        <Field label="Description">
          <textarea
            rows={3}
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
            placeholder="What to expect, what to bring, who to ask."
          />
        </Field>

        <div className="grid two">
          <Field label="Starts">
            <input
              type="datetime-local"
              value={form.startsAt}
              onChange={(e) => setForm({ ...form, startsAt: e.target.value })}
              required
            />
          </Field>
          <Field label="Ends" hint="Optional.">
            <input
              type="datetime-local"
              value={form.endsAt}
              onChange={(e) => setForm({ ...form, endsAt: e.target.value })}
            />
          </Field>
          <Field label="Location">
            <input
              value={form.location}
              onChange={(e) => setForm({ ...form, location: e.target.value })}
              placeholder="Community green"
            />
          </Field>
          <Field label="RSVP limit" hint="Total headcount including guests. Leave blank for no cap.">
            <input
              type="number"
              min={1}
              value={form.rsvpLimit}
              onChange={(e) => setForm({ ...form, rsvpLimit: e.target.value })}
              disabled={!form.rsvpEnabled}
            />
          </Field>
        </div>

        <label className="check">
          <input
            type="checkbox"
            checked={form.rsvpEnabled}
            onChange={(e) => setForm({ ...form, rsvpEnabled: e.target.checked })}
          />
          <span>Collect RSVPs</span>
        </label>
        <label className="check">
          <input
            type="checkbox"
            checked={form.commentsEnabled}
            onChange={(e) => setForm({ ...form, commentsEnabled: e.target.checked })}
          />
          <span>Allow comments</span>
        </label>
        <label className="check">
          <input
            type="checkbox"
            checked={form.allDay}
            onChange={(e) => setForm({ ...form, allDay: e.target.checked })}
          />
          <span>All-day event</span>
        </label>
        <label className="check">
          <input
            type="checkbox"
            checked={form.published}
            onChange={(e) => setForm({ ...form, published: e.target.checked })}
          />
          <span>Publish now — unpublished events are visible only to you</span>
        </label>

        <span className="label-text">Flyer or photos</span>
        <AttachmentUploader value={files} onChange={setFiles} max={5} />

        <div className="btn-row">
          <button type="submit" className="btn" disabled={busy}>
            {busy ? 'Saving…' : event ? 'Save changes' : 'Publish event'}
          </button>
          <button type="button" className="btn ghost" onClick={onCancel}>
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
}

/* --------------------------------------------------------------------- card */

function EventCard({
  event,
  onChange,
  onEdit,
  onDeleted,
}: {
  event: CommunityEvent;
  onChange: (event: CommunityEvent) => void;
  onEdit: () => void;
  onDeleted: () => void;
}) {
  const { user, isBoard, canOrganise } = useAuth();
  const { notify, fail } = useToast();
  const [guests, setGuests] = useState(
    event.rsvps.find((rsvp) => rsvp.userId === user?.id)?.guests ?? 0,
  );
  const [busy, setBusy] = useState(false);
  const [showComments, setShowComments] = useState(false);

  const canEdit = canOrganise && (event.createdBy === user?.id || isBoard);
  const full =
    event.rsvpLimit !== null && event.goingCount >= event.rsvpLimit && event.myRsvp !== 'going';

  async function rsvp(status: 'going' | 'maybe' | 'not_going' | null) {
    setBusy(true);
    try {
      const { event: updated } = await api.eventRsvp(event.id, status, guests);
      onChange(updated);
    } catch (error) {
      fail(error, 'Your RSVP was not saved.');
    } finally {
      setBusy(false);
    }
  }

  async function remove() {
    if (!confirm(`Cancel "${event.title}"? Everyone's RSVP will be removed.`)) return;
    try {
      await api.eventDelete(event.id);
      onDeleted();
      notify('Event cancelled.');
    } catch (error) {
      fail(error, 'The event was not cancelled.');
    }
  }

  return (
    <div className="card">
      <div className="card-head">
        <h3>{event.title}</h3>
        <div className="tag-row">
          {!event.published && <Tag tone="warn">Draft</Tag>}
          {event.rsvpEnabled && (
            <Tag tone={full ? 'bad' : 'info'}>
              {full ? 'Full' : `${event.goingCount} going`}
              {event.rsvpLimit !== null ? ` / ${event.rsvpLimit}` : ''}
            </Tag>
          )}
        </div>
      </div>

      <div className="mut">
        {dateTimeLabel(event.startsAt, event.allDay)}
        {event.endsAt && !event.allDay
          ? ` – ${new Date(event.endsAt).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`
          : ''}
        {event.location ? ` · ${event.location}` : ''} · hosted by {event.createdByName}
      </div>

      {event.description && <p className="pre-wrap">{event.description}</p>}
      <AttachmentGrid items={event.attachments} />

      {event.rsvpEnabled && (
        <div className="btn-row">
          <button
            type="button"
            className={event.myRsvp === 'going' ? 'btn' : 'btn s'}
            disabled={busy || (full && event.myRsvp !== 'going')}
            onClick={() => void rsvp(event.myRsvp === 'going' ? null : 'going')}
          >
            {event.myRsvp === 'going' ? '✓ Going' : 'RSVP'}
          </button>
          <button
            type="button"
            className={event.myRsvp === 'maybe' ? 'btn' : 'btn s'}
            disabled={busy}
            onClick={() => void rsvp(event.myRsvp === 'maybe' ? null : 'maybe')}
          >
            Maybe
          </button>
          {event.myRsvp === 'going' && (
            <label className="row" style={{ gap: 6 }}>
              <span className="mut">Guests</span>
              <input
                type="number"
                min={0}
                max={20}
                value={guests}
                onChange={(e) => setGuests(Number(e.target.value))}
                onBlur={() => void rsvp('going')}
                style={{ width: 80, margin: 0 }}
              />
            </label>
          )}
        </div>
      )}

      {event.rsvpEnabled && event.rsvps.filter((r) => r.status === 'going').length > 0 && (
        <p className="mut" style={{ marginTop: 10 }}>
          Going:{' '}
          {event.rsvps
            .filter((r) => r.status === 'going')
            .map((r) => (r.guests > 0 ? `${r.userName} +${r.guests}` : r.userName))
            .join(', ')}
        </p>
      )}

      <div className="btn-row">
        {event.commentsEnabled && (
          <button type="button" className="btn s tiny" onClick={() => setShowComments((open) => !open)}>
            {showComments
              ? 'Hide comments'
              : `Comments${event.notes.length > 0 ? ` (${event.notes.length})` : ''}`}
          </button>
        )}
        {canEdit && (
          <>
            <button type="button" className="btn ghost tiny" onClick={onEdit}>
              Edit
            </button>
            <button type="button" className="btn ghost tiny" onClick={() => void remove()}>
              Cancel event
            </button>
          </>
        )}
      </div>

      {showComments && event.commentsEnabled && (
        <NoteThread
          notes={event.notes}
          placeholder="Ask a question or share something…"
          onSubmit={async (body, _internal, attachmentIds) => {
            const { event: updated } = await api.eventComment(event.id, body, attachmentIds);
            onChange(updated);
          }}
        />
      )}
    </div>
  );
}
